/**
 * Utilities for extracting and parsing metadata from observability log items
 */

import { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';

// Helper types for value wrappers
export type ValueWrapper<T> = { value?: T };

export const isValueWrapper = <T>(x: unknown): x is ValueWrapper<T> => {
  return typeof x === 'object' && x !== null && 'value' in (x as Record<string, unknown>);
};

export const unwrap = <T>(input: T | ValueWrapper<T> | undefined): T | undefined => {
  if (isValueWrapper<T>(input)) {
    return input.value as T | undefined;
  }
  return input as T | undefined;
};

// Metadata document shape
export type MetaDoc = {
  [key: string]: unknown;
  filter_response?: { value?: { action?: string; reason?: string; resource_violations?: Record<string, unknown> } };
  overall_action?: { value?: string };
  overall_action_early?: { value?: string };
  attachments_action?: { value?: string };
  router_question_path?: ValueWrapper<boolean> | boolean | undefined;
  model_to_use_for_chat?: ValueWrapper<string>;
  model_name_for_generation?: ValueWrapper<string>;
  agent_name?: ValueWrapper<string>;
  response_text?: ValueWrapper<string>;
  clean_text?: ValueWrapper<string>;
  slack_data?: {
    slack_user_name?: string;
    slack_channel_name?: string;
    query?: string;
    is_mentioned?: ValueWrapper<boolean> | boolean;
  };
  message?: ValueWrapper<string>;
  user_message_text?: ValueWrapper<string>;
  status_code?: number | string;
  files_for_chat?: ValueWrapper<unknown[]> | unknown[];
  attachment_results?: ValueWrapper<unknown[]> | unknown[];
};

/**
 * Safely access nested property via dot path and unwrap ValueWrapper
 */
export function getByPath(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const segments = path.split('.');
  let cur: unknown = obj;
  for (const seg of segments) {
    if (isValueWrapper<unknown>(cur)) {
      cur = (cur as ValueWrapper<unknown>).value;
    }
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  if (isValueWrapper<unknown>(cur)) {
    return (cur as ValueWrapper<unknown>).value;
  }

  return cur;
}

/**
 * Coerce a value to string if possible
 */
export function coerceToString(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    return undefined; // Avoid dumping huge JSON
  }
  return undefined;
}

/**
 * Pick the first non-empty string from a list of paths
 */
export function pickFirstString(source: Record<string, unknown> | MetaDoc, paths: string[]): string | undefined {
  for (const p of paths) {
    const v = getByPath(source, p);
    const s = coerceToString(v);
    if (s != null && s !== '') return s;
  }
  return undefined;
}

/**
 * Pick the first boolean value from a list of paths
 */
export function pickFirstBoolean(source: Record<string, unknown> | MetaDoc, paths: string[]): boolean | undefined {
  for (const p of paths) {
    const v = getByPath(source, p);
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const lower = v.toLowerCase();
      if (lower === 'true') return true;
      if (lower === 'false') return false;
    }
    if (typeof v === 'number') {
      if (v === 1) return true;
      if (v === 0) return false;
    }
  }
  return undefined;
}

/**
 * Pick the first number or string from a list of paths
 */
export function pickFirstNumberOrString(
  source: Record<string, unknown> | MetaDoc,
  paths: string[]
): number | string | undefined {
  for (const p of paths) {
    const v = getByPath(source, p);
    if (typeof v === 'number') return v;
    const s = coerceToString(v);
    if (s != null && s !== '') return s;
  }
  return undefined;
}

/**
 * Derive message text from metadata, checking multiple locations
 */
export function deriveMessageText(md: MetaDoc): string | undefined {
  // Prefer Slack raw query if available
  const q = pickFirstString(md, ['slack_data.query']);
  if (q) return q;

  // Try messages_for_filtering.value: find last user role
  const arr = getByPath(md, 'messages_for_filtering.value');
  if (Array.isArray(arr)) {
    for (let i = arr.length - 1; i >= 0; i -= 1) {
      const entry = arr[i] as Record<string, unknown>;
      const role = coerceToString(entry?.role);
      if (role === 'user') {
        const content = entry?.content;
        if (typeof content === 'string') {
          const m = content.match(/<content>([\s\S]*?)<\/content>/i);
          return (m?.[1] || content).trim();
        }
        if (content && typeof content === 'object') {
          const text = coerceToString((content as Record<string, unknown>)?.text);
          if (text) return text.trim();
        }
      }
    }
  }
  return undefined;
}

/**
 * Derive assistant response text from metadata
 */
export function deriveAssistantText(md: MetaDoc): string | undefined {
  const arr = getByPath(md, 'messages_for_filtering.value');
  if (Array.isArray(arr)) {
    for (let i = arr.length - 1; i >= 0; i -= 1) {
      const entry = arr[i] as Record<string, unknown>;
      const role = coerceToString(entry?.role);
      if (role === 'assistant') {
        const content = entry?.content;
        if (typeof content === 'string') {
          const m = content.match(/<content>([\s\S]*?)<\/content>/i);
          return (m?.[1] || content).trim();
        }
        if (content && typeof content === 'object') {
          const text = coerceToString((content as Record<string, unknown>)?.text);
          if (text) return text.trim();
        }
      }
    }
  }
  return undefined;
}

/**
 * Derive user string from various possible locations
 */
export function deriveUserString(item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc): string | undefined {
  const candidates: Array<unknown> = [
    getByPath(item as unknown, 'user.email'),
    getByPath(item as unknown, 'user.username'),
    getByPath(item as unknown, 'user.name'),
    getByPath(item as unknown, 'user.real_name'),
    getByPath(item as unknown, 'slack_user.name'),
    getByPath(item as unknown, 'slack_user.real_name'),
    pickFirstString(md, ['slack_data.slack_user_name', 'slack_user.name', 'user.email', 'user.username', 'user']),
  ];
  for (const c of candidates) {
    const s = coerceToString(c);
    if (s != null && s !== '') return s;
  }
  return undefined;
}

/**
 * Extract metadata from item, merging logs if present
 */
export function extractItemMetadata(item: ObservabilityLogsRetrieve200ResultsItem): Record<string, unknown> {
  const fromLogs = mergeItemMetadata(item);
  const direct = (item as unknown as Record<string, unknown>)?.metadata;
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return { ...fromLogs, ...(direct as Record<string, unknown>) };
  }
  return fromLogs;
}

/**
 * Merge metadata from all logs into a single object
 * Prioritizes the most recent log (last in array) and falls back to earlier logs for missing keys
 */
function mergeItemMetadata(item: ObservabilityLogsRetrieve200ResultsItem): Record<string, unknown> {
  const acc: Record<string, unknown> = {};
  try {
    const logs = (item?.logs ?? []) as Array<{ metadata?: unknown } & Record<string, unknown>>;

    // Start from the last log (most recent) and work backwards
    // This ensures most recent data takes priority, but we preserve older data as fallback
    for (let i = logs.length - 1; i >= 0; i--) {
      const e = logs[i];
      if (e && e.metadata && typeof e.metadata === 'object' && !Array.isArray(e.metadata)) {
        const metadata = e.metadata as Record<string, unknown>;
        // Only add keys that don't exist yet (fallback behavior)
        for (const key of Object.keys(metadata)) {
          if (!(key in acc)) {
            acc[key] = metadata[key];
          }
        }
      }
    }
  } catch {
    // ignore merging errors
  }
  return acc;
}
